import { useState, useEffect, useRef } from 'react';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  doc,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import styles from './ChatWidget.module.css';

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const { currentUser } = useAuth();
  const { t } = useTranslation();

  // Écouter les messages non lus même quand le chat est fermé
  useEffect(() => {
    if (!currentUser || isOpen) {
      // Si le chat est ouvert, on ne compte pas les messages non lus ici
      return;
    }

    let unsubscribeMessages = null;

    const initUnreadListener = async () => {
      try {
        const userId = currentUser.uid;

        // Chercher une conversation existante pour cet utilisateur
        const conversationsRef = collection(db, 'chatConversations');
        const q = query(
          conversationsRef,
          where('userId', '==', userId),
          where('status', 'in', ['active', 'pending']),
        );

        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const convId = snapshot.docs[0].id;
          setConversationId(convId);

          // Écouter les messages de l'admin non lus
          const messagesRef = collection(db, 'chatConversations', convId, 'messages');
          const unreadQuery = query(
            messagesRef,
            where('sender', '==', 'admin'),
            where('read', '==', false),
          );

          unsubscribeMessages = onSnapshot(unreadQuery, (messagesSnapshot) => {
            setUnreadCount(messagesSnapshot.size);
          });
        } else {
          setUnreadCount(0);
          setConversationId(null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erreur lors de l'écoute des messages non lus:", error);
        setUnreadCount(0);
      }
    };

    initUnreadListener();

    return () => {
      if (unsubscribeMessages) {
        unsubscribeMessages();
      }
    };
  }, [currentUser, isOpen]);

  // Récupérer ou créer une conversation pour l'utilisateur connecté (quand le chat est ouvert)
  useEffect(() => {
    if (!isOpen || !currentUser) return;

    let unsubscribeMessages = null;

    const initConversation = async () => {
      try {
        const userId = currentUser.uid;
        const userEmail = currentUser.email || '';
        const userDisplayName =
          currentUser.displayName || userEmail.split('@')[0] || 'Utilisateur';

        // Chercher une conversation existante pour cet utilisateur
        const conversationsRef = collection(db, 'chatConversations');
        const q = query(
          conversationsRef,
          where('userId', '==', userId),
          where('status', 'in', ['active', 'pending']),
        );

        const snapshot = await getDocs(q);

        let convId = null;
        if (!snapshot.empty) {
          convId = snapshot.docs[0].id;
        } else {
          // Créer une nouvelle conversation
          const newConv = await addDoc(conversationsRef, {
            userId,
            userDisplayName,
            userEmail,
            status: 'pending',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            unreadCount: 0,
          });
          convId = newConv.id;
        }

        setConversationId(convId);

        // Écouter les messages de cette conversation
        const messagesRef = collection(db, 'chatConversations', convId, 'messages');
        const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));

        unsubscribeMessages = onSnapshot(messagesQuery, (snapshot) => {
          const msgs = snapshot.docs.map((m) => ({
            id: m.id,
            ...m.data(),
          }));
          setMessages(msgs);

          // Marquer la conversation comme lue côté utilisateur
          if (convId) {
            // Marquer tous les messages de l'admin comme lus
            msgs.forEach((msg) => {
              if (msg.sender === 'admin' && !msg.read) {
                updateDoc(doc(db, 'chatConversations', convId, 'messages', msg.id), {
                  read: true,
                });
              }
            });

            updateDoc(doc(db, 'chatConversations', convId), {
              unreadCount: 0,
              updatedAt: serverTimestamp(),
            });
            setUnreadCount(0);
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erreur lors de l'initialisation de la conversation:", error);
      }
    };

    initConversation();

    return () => {
      if (unsubscribeMessages) {
        unsubscribeMessages();
      }
    };
  }, [isOpen, currentUser]);

  // Scroll automatique vers le bas
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversationId || sending) return;

    setSending(true);
    try {
      const messagesRef = collection(db, 'chatConversations', conversationId, 'messages');
      await addDoc(messagesRef, {
        text: newMessage.trim(),
        sender: 'user',
        userId: currentUser.uid,
        userDisplayName:
          currentUser.displayName ||
          currentUser.email?.split('@')[0] ||
          'Utilisateur',
        userEmail: currentUser.email || '',
        createdAt: serverTimestamp(),
        read: false,
      });

      // Mettre à jour la conversation (dernier message = utilisateur)
      await updateDoc(doc(db, 'chatConversations', conversationId), {
        updatedAt: serverTimestamp(),
        unreadCount: 1,
        status: 'active',
        lastMessageSender: 'user',
      });

      setNewMessage('');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erreur lors de l\'envoi du message:', error);
      alert('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  // Le chat n'est disponible que pour les utilisateurs connectés
  if (!currentUser) {
    return null;
  }

  if (!isOpen) {
    return (
      <button
        type="button"
        className={styles.chatButton}
        onClick={() => setIsOpen(true)}
        aria-label="Ouvrir le chat"
        style={{ zIndex: 10000 }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
        <span className={styles.chatButtonText}>Chat</span>
        {unreadCount > 0 && (
          <span className={styles.unreadBadge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>
    );
  }

  return (
    <div className={styles.chatWidget}>
      <div className={styles.chatHeader}>
        <div className={styles.chatHeaderInfo}>
          <h3>{t('chat.title')}</h3>
          <p>{t('chat.subtitle')}</p>
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={() => {
            setIsOpen(false);
          }}
          aria-label={t('common.close')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <>
        <div className={styles.messagesContainer}>
          {messages.length === 0 ? (
            <div className={styles.emptyState}>
              <p>{t('chat.emptyState')}</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`${styles.message} ${
                  msg.sender === 'user' ? styles.messageVisitor : styles.messageAdmin
                }`}
              >
                <div className={styles.messageContent}>
                  <p>{msg.text}</p>
                  <span className={styles.messageTime}>{formatTime(msg.createdAt)}</span>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSendMessage} className={styles.messageForm}>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={t('chat.messagePlaceholder')}
            className={styles.messageInput}
            disabled={sending}
          />
          <button
            type="submit"
            className={styles.sendButton}
            disabled={!newMessage.trim() || sending}
          >
            {sending ? '...' : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            )}
          </button>
        </form>
      </>
    </div>
  );
}

export default ChatWidget;

